import { createContext, useContext, useEffect, useRef, useState } from "react";
import { ChevronDown, History, Lock } from "lucide-react";
import { toast } from "sonner";

// True inside a Settings area the current user can browse but not change (Redline-managed dial).
// Every SettingsSection under it shows a "View only" pill so it's obvious BEFORE you try a switch.
export const SettingsReadOnly = createContext(false);

// The controls `.rl-view-only` (index.css) makes inert — kept in sync with that rule.
const INERT_CONTROLS = 'input, select, textarea, [role="switch"], [role="checkbox"], [role="radio"], [role="slider"], [role="combobox"], button:not([data-testid="info-tip"]):not([aria-expanded]):not([role="tab"])';
const LOCKED_TITLE = "Managed by Ask Redline — you can see this but not change it";

// Why a card is view-only. The SettingsReadOnly value is `false` or one of these (any truthy = locked):
//   "redline"      — the community's dial keeps this with Ask Redline
//   "board-grant"  — the dial is open to board members, but this seat lacks "Can configure"
//   "board"        — the dial is open to managers only; board seats browse
const REASONS = {
  redline: { title: "Managed by Ask Redline", body: "Browse freely — changes go through our team.", request: true },
  "board-grant": { title: "Your board seat is view-only here", body: "A Community Manager can turn on “Can configure” for you on the Team page.", request: false },
  board: { title: "Board seats are view-only here", body: "Changes to these settings are made by your Community Managers.", request: false },
};
const reasonMeta = (reason) => REASONS[reason] || REASONS.redline;

function explainNow(reason) {
  const m = reasonMeta(reason);
  const canRequest = m.request && Boolean(document.querySelector('[data-testid="settings-lock-banner"]'));
  toast(m.title, {
    id: "rl-locked", duration: 5000, description: m.body,
    action: canRequest ? { label: "Request a change →", onClick: () => window.dispatchEvent(new CustomEvent("rl:request-change")) } : undefined,
  });
}

// A click that lands where a greyed-out control sits (they don't receive pointer events, so the wrapper
// gets the click) → explain, right there, why nothing happened — and offer the change-request dialog.
export function explainLocked(e, scope, reason) {
  const root = scope || e.currentTarget;
  const x = e.clientX, y = e.clientY;
  const hit = Array.from(root.querySelectorAll(INERT_CONTROLS)).some((el) => {
    const r = el.getBoundingClientRect();
    return r.width > 0 && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  });
  if (!hit) return false;
  explainNow(reason);
  return true;
}

// Spread onto a view-only wrapper: mouse clicks explain; keyboard users who tab into a greyed control
// can't activate or type into it either (the server would 403, but they shouldn't get that far).
export function lockedProps(reason) {
  const isControl = (t) => t instanceof Element && t.matches(INERT_CONTROLS);
  return {
    onClick: (e) => explainLocked(e, undefined, reason),
    onKeyDownCapture: (e) => {
      if (!isControl(e.target) || e.key === "Tab" || e.key === "Escape") return;
      e.preventDefault(); e.stopPropagation(); explainNow(reason);
    },
    onBeforeInputCapture: (e) => { if (isControl(e.target)) { e.preventDefault(); e.stopPropagation(); explainNow(reason); } },
  };
}

// The one "View only" pill every locked card wears — ink so it reads as a state, not a hint.
export function ViewOnlyPill({ testid, className = "", reason }) {
  const m = reason ? reasonMeta(reason) : null;
  return (
    <span data-testid={testid} title={m ? `${m.title} — ${m.body}` : LOCKED_TITLE}
      className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-ink text-white font-body ${className}`}>
      <Lock className="h-3 w-3" strokeWidth={2.25} /> View only
    </span>
  );
}

// "Only Ask Redline staff can CHANGE this setting" — says nothing about who the setting applies to.
// Replaces the old bare "Redline only" chip, which sat after sentences like "…can upload & edit AI
// documents" and read as if Redline were the only ones allowed to upload.
export const SET_BY_REDLINE_TITLE = "Only Ask Redline staff can change this setting. It doesn't limit who the setting applies to — the value next to it does.";
export function SetByRedline({ testid, className = "" }) {
  return (
    <span data-testid={testid} title={SET_BY_REDLINE_TITLE}
      className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-sand-100 text-ink/70 font-body ${className}`}>
      <Lock className="h-3 w-3" strokeWidth={2.25} /> Set by Ask Redline
    </span>
  );
}

// Lock badge pinned to a card's icon tile when the card is view-only.
export function LockedTile({ children, locked, className = "" }) {
  return (
    <span className={`relative shrink-0 ${className}`}>
      {children}
      {locked && <span aria-hidden className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-ink text-white ring-2 ring-white flex items-center justify-center"><Lock className="h-2.5 w-2.5" strokeWidth={2.5} /></span>}
    </span>
  );
}

const TONES = {
  redline: "bg-redline-50 text-redline-500", violet: "bg-violet-50 text-violet-600", sky: "bg-sky-50 text-sky-700",
  emerald: "bg-emerald-50 text-emerald-700", amber: "bg-amber-50 text-amber-700", sand: "bg-sand-100 text-ink/60",
};

// Open/closed state remembered per card (localStorage), so Settings opens the way you left it.
// `?section=<id>` in the URL (deep-links from module ⚙ buttons) opens that card.
export const wantedSection = () => { try { return new URLSearchParams(window.location.search).get("section") || ""; } catch { return ""; } };

export function useOpenState(id, def = false) {
  const k = `settings.open.${id}`;
  const [open, setOpen] = useState(() => {
    if (wantedSection() === id) return true;
    try { const v = localStorage.getItem(k); return v == null ? def : v === "1"; } catch { return def; }
  });
  const toggle = () => setOpen((o) => { try { localStorage.setItem(k, o ? "0" : "1"); } catch {} return !o; });
  return [open, toggle];
}

// Every Settings card: icon · title · one-line summary · chevron. Collapsed by default, remembers
// its state. `headerRight` sits in the header on wide screens and above the body on phones.
export function SettingsSection({ id, icon: Icon, tone = "redline", title, summary, badges, headerRight, defaultOpen = false, children, testid }) {
  const [open, toggle] = useOpenState(id, defaultOpen);
  const readOnly = useContext(SettingsReadOnly);
  const tid = testid || id;
  const ref = useRef(null);
  useEffect(() => {
    if (wantedSection() === id && ref.current) setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
  }, [id]);
  const headerRightRef = useRef(null);
  // A click on the greyed header switch explains instead of toggling the card.
  const onHeaderClick = (e) => {
    if (readOnly && headerRightRef.current && explainLocked(e, headerRightRef.current, readOnly)) return;
    toggle();
  };
  return (
    <section ref={ref} className={`bg-white rounded-2xl border overflow-hidden ${readOnly ? "border-sand-300" : "border-sand-200"} ${wantedSection() === id ? "ring-2 ring-redline-500/30" : ""}`} data-testid={tid} data-locked={readOnly || undefined}>
      <div role="button" tabIndex={0} aria-expanded={open} data-testid={`${tid}-toggle`} onClick={onHeaderClick}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } }}
        className={`w-full flex items-center gap-3 p-4 sm:p-5 md:px-6 text-left cursor-pointer transition-colors select-none ${readOnly ? "bg-sand-50/80 hover:bg-sand-100/70" : "hover:bg-sand-50"}`}>
        {Icon && <LockedTile locked={readOnly}><span className={`h-9 w-9 rounded-xl flex items-center justify-center ${readOnly ? TONES.sand : (TONES[tone] || TONES.redline)}`}><Icon className="h-[18px] w-[18px]" strokeWidth={1.5} /></span></LockedTile>}
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-[15px] leading-snug flex items-center gap-2 flex-wrap">
            <span>{title}</span>
            {readOnly && <ViewOnlyPill testid={`${tid}-view-only`} reason={readOnly} />}
            {badges && badges.filter(Boolean).map((b, i) => (
              <span key={i} data-testid={`${tid}-badge-${i}`} className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${b.warn ? "bg-amber-100 text-amber-700" : b.on ? "bg-emerald-100 text-emerald-700" : "bg-sand-100 text-ink/60"}`}>{b.text}</span>
            ))}
          </div>
          {summary && <div data-testid={`${tid}-summary`} className={`text-xs text-muted-foreground mt-0.5 ${open ? "" : "line-clamp-1"}`}>{summary}</div>}
        </div>
        {headerRight && <span ref={headerRightRef} className={`hidden sm:inline-flex shrink-0 ${readOnly ? "rl-view-only" : ""}`} onClick={(e) => { if (!readOnly) e.stopPropagation(); }} onKeyDown={(e) => e.stopPropagation()}>{headerRight}</span>}
        <ChevronDown className={`h-5 w-5 text-muted-foreground shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </div>
      {open && (
        // Browsing is always allowed — only the CONTROLS go inert in a view-only area (nested cards, info tips and links keep working).
        <div className={`px-4 sm:px-5 md:px-6 pb-5 sm:pb-6 pt-4 space-y-4 border-t border-sand-100 ${readOnly ? "rl-view-only" : ""}`} data-testid={`${tid}-body`} {...(readOnly ? lockedProps(readOnly) : {})}>
          {headerRight && <div className="sm:hidden flex justify-end">{headerRight}</div>}
          {children}
        </div>
      )}
    </section>
  );
}

const rel = (iso) => {
  if (!iso) return "";
  const d = new Date(iso); const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 90) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)} h ago`;
  if (diff < 86400 * 7) return `${Math.round(diff / 86400)} d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: d.getFullYear() === new Date().getFullYear() ? undefined : "numeric" });
};

// "Turned off by Rocky · 3 d ago" — from GET /tenants/{id}/settings-history.
export function LastChanged({ entry, testid }) {
  if (!entry) return null;
  const v = String(entry.value).toLowerCase();
  const verb = v === "true" ? "Turned on" : v === "false" ? "Turned off" : `Set to ${entry.value}`;
  return (
    <div className="mt-1 flex items-center gap-1 text-[11px] text-ink/45" data-testid={testid} title={entry.at ? new Date(entry.at).toLocaleString() : ""}>
      <History className="h-3 w-3" /> {verb} by <span className="font-semibold text-ink/60">{entry.user}</span> · {rel(entry.at)}
    </div>
  );
}
